/**
 * JobContext - Gestionnaire global des jobs de génération
 * 
 * Résout les problèmes suivants :
 * - État perdu lors du changement de page
 * - Polling qui se chevauche (setInterval + async)
 * - Jobs non restaurés après rechargement
 * - Duplication du polling entre composants
 * - Duplication du polling entre onglets (BroadcastChannel)
 * 
 * Architecture :
 * - Un seul polling par job (setTimeout récursif, pas setInterval)
 * - Restauration automatique des jobs actifs au démarrage
 * - Survit aux changements de route
 * - Backoff exponentiel après erreur réseau
 * - Coordination multi-onglets via BroadcastChannel
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Configuration du polling
const POLL_INTERVAL_MS = 2000;
const POLL_INTERVAL_HIDDEN_MS = 10000; // Ralentir quand onglet masqué
const MAX_BACKOFF_MS = 30000;
const INITIAL_BACKOFF_MS = 2000;

// Nom du canal pour la coordination multi-onglets
const BROADCAST_CHANNEL_NAME = 'autoedito_jobs_sync';

// Types de messages BroadcastChannel
const MSG_TYPE = {
  JOB_UPDATE: 'JOB_UPDATE',           // Mise à jour d'un job
  JOB_REGISTERED: 'JOB_REGISTERED',   // Nouveau job enregistré
  JOB_COMPLETED: 'JOB_COMPLETED',     // Job terminé
  POLLING_CLAIM: 'POLLING_CLAIM',     // Un onglet réclame le polling
  POLLING_RELEASE: 'POLLING_RELEASE', // Un onglet libère le polling
  REQUEST_STATE: 'REQUEST_STATE',     // Demande de l'état actuel
  STATE_RESPONSE: 'STATE_RESPONSE',   // Réponse avec l'état
};

const JobContext = createContext(null);

export const useJobs = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};

export const JobProvider = ({ children }) => {
  // Map des jobs actifs : { jobId: { status, progress, sites_status, ... } }
  const [activeJobs, setActiveJobs] = useState({});
  
  // Set des jobIds en cours de polling (évite les doublons)
  const pollingJobsRef = useRef(new Set());
  
  // Backoff par job après erreur réseau
  const backoffRef = useRef({});
  
  // Ref pour accéder à l'état actuel dans les callbacks
  const activeJobsRef = useRef(activeJobs);
  useEffect(() => {
    activeJobsRef.current = activeJobs;
  }, [activeJobs]);

  // Callbacks de notification (optionnels, pour les composants qui écoutent)
  const listenersRef = useRef({});
  
  // BroadcastChannel pour la coordination multi-onglets
  const channelRef = useRef(null);
  
  // ID unique de cet onglet
  const tabIdRef = useRef(`tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Onglet leader pour le polling (un seul onglet poll à la fois)
  const [isLeader, setIsLeader] = useState(false);
  const leaderTabIdRef = useRef(null);
  const leaderHeartbeatRef = useRef(null);

  // Visibilité de l'onglet
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
  
  useEffect(() => {
    const handleVisibility = () => setIsTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  /**
   * Initialise le BroadcastChannel pour la coordination multi-onglets
   */
  useEffect(() => {
    // Vérifier si BroadcastChannel est supporté
    if (typeof BroadcastChannel === 'undefined') {
      console.log('[JobContext] BroadcastChannel not supported, single-tab mode');
      setIsLeader(true);
      return;
    }

    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const { type, payload, senderId } = event.data;
      
      // Ignorer les messages de cet onglet
      if (senderId === tabIdRef.current) return;

      switch (type) {
        case MSG_TYPE.JOB_UPDATE:
          // Mettre à jour l'état local avec les données reçues
          setActiveJobs(prev => ({
            ...prev,
            [payload.jobId]: { ...prev[payload.jobId], ...payload.data }
          }));
          // Notifier les listeners locaux
          notifyListenersFromBroadcast(payload.jobId, payload.data);
          break;

        case MSG_TYPE.JOB_REGISTERED:
          // Un autre onglet a créé un job
          setActiveJobs(prev => ({
            ...prev,
            [payload.jobId]: payload.data
          }));
          break;

        case MSG_TYPE.JOB_COMPLETED:
          // Marquer le job comme terminé
          setActiveJobs(prev => ({
            ...prev,
            [payload.jobId]: { ...prev[payload.jobId], ...payload.data, polling: false }
          }));
          break;

        case MSG_TYPE.POLLING_CLAIM:
          // Un autre onglet réclame le leadership
          if (!isLeader || payload.priority > (isTabVisible ? 1 : 0)) {
            leaderTabIdRef.current = senderId;
            setIsLeader(false);
          }
          break;

        case MSG_TYPE.POLLING_RELEASE:
          // Le leader actuel libère le polling
          if (leaderTabIdRef.current === senderId) {
            leaderTabIdRef.current = null;
            // Essayer de devenir leader
            tryBecomeLeader();
          }
          break;

        case MSG_TYPE.REQUEST_STATE:
          // Un nouvel onglet demande l'état actuel
          if (isLeader && Object.keys(activeJobsRef.current).length > 0) {
            broadcast(MSG_TYPE.STATE_RESPONSE, { jobs: activeJobsRef.current });
          }
          break;

        case MSG_TYPE.STATE_RESPONSE:
          // Recevoir l'état d'un autre onglet
          if (Object.keys(activeJobsRef.current).length === 0 && payload.jobs) {
            setActiveJobs(payload.jobs);
          }
          break;

        default:
          break;
      }
    };

    // Demander l'état actuel aux autres onglets
    broadcast(MSG_TYPE.REQUEST_STATE, {});
    
    // Essayer de devenir leader après un court délai
    const leaderTimeout = setTimeout(() => tryBecomeLeader(), 500);

    // Heartbeat pour maintenir le leadership
    const heartbeatInterval = setInterval(() => {
      if (isLeader) {
        broadcast(MSG_TYPE.POLLING_CLAIM, { priority: isTabVisible ? 1 : 0 });
      }
    }, 5000);
    leaderHeartbeatRef.current = heartbeatInterval;

    return () => {
      clearTimeout(leaderTimeout);
      clearInterval(heartbeatInterval);
      
      // Libérer le leadership avant de fermer
      if (isLeader) {
        broadcast(MSG_TYPE.POLLING_RELEASE, {});
      }
      
      channel.close();
      channelRef.current = null;
    };
  }, [isLeader, isTabVisible]);

  /**
   * Envoie un message à tous les autres onglets
   */
  const broadcast = useCallback((type, payload) => {
    if (channelRef.current) {
      channelRef.current.postMessage({
        type,
        payload,
        senderId: tabIdRef.current,
        timestamp: Date.now()
      });
    }
  }, []);

  /**
   * Essaye de devenir l'onglet leader pour le polling
   */
  const tryBecomeLeader = useCallback(() => {
    if (!leaderTabIdRef.current) {
      leaderTabIdRef.current = tabIdRef.current;
      setIsLeader(true);
      broadcast(MSG_TYPE.POLLING_CLAIM, { priority: isTabVisible ? 1 : 0 });
    }
  }, [broadcast, isTabVisible]);

  /**
   * Notifie les listeners locaux depuis un message broadcast
   */
  const notifyListenersFromBroadcast = useCallback((jobId, jobData) => {
    listenersRef.current[jobId]?.forEach(callback => {
      try {
        callback(jobData);
      } catch (e) {
        console.error('[JobContext] Listener error from broadcast:', e);
      }
    });
  }, []);

  /**
   * Enregistre un listener pour un job spécifique
   * @returns fonction de désinscription
   */
  const subscribeToJob = useCallback((jobId, callback) => {
    if (!listenersRef.current[jobId]) {
      listenersRef.current[jobId] = new Set();
    }
    listenersRef.current[jobId].add(callback);
    
    // Solution A: Appel immédiat avec l'état actuel si le job existe déjà
    // Garantit que l'UI affiche l'état même si le job est déjà terminé
    const currentState = activeJobsRef.current[jobId];
    if (currentState) {
      callback(currentState);
    }
    
    // Retourner la fonction de désinscription
    return () => {
      listenersRef.current[jobId]?.delete(callback);
      if (listenersRef.current[jobId]?.size === 0) {
        delete listenersRef.current[jobId];
      }
    };
  }, []);

  /**
   * Notifie les listeners d'un job
   */
  const notifyListeners = useCallback((jobId, jobData) => {
    listenersRef.current[jobId]?.forEach(callback => {
      try {
        callback(jobData);
      } catch (e) {
        console.error('[JobContext] Listener error:', e);
      }
    });
  }, []);

  /**
   * Met à jour un job et notifie les listeners (+ broadcast aux autres onglets)
   */
  const updateJob = useCallback((jobId, updates, skipBroadcast = false) => {
    setActiveJobs(prev => {
      const current = prev[jobId] || {};
      const updated = { ...current, ...updates, lastUpdated: Date.now() };
      const newState = { ...prev, [jobId]: updated };
      
      // Solution B: Notifier les listeners immédiatement (sans setTimeout)
      // Évite les délais qui peuvent causer des affichages manqués
      notifyListeners(jobId, updated);
      
      // Broadcaster aux autres onglets (sauf si c'est une mise à jour reçue d'un autre onglet)
      if (!skipBroadcast) {
        broadcast(MSG_TYPE.JOB_UPDATE, { jobId, data: updated });
      }
      
      return newState;
    });
  }, [notifyListeners, broadcast]);

  /**
   * Supprime un job de l'état actif
   */
  const removeJob = useCallback((jobId) => {
    setActiveJobs(prev => {
      const { [jobId]: removed, ...rest } = prev;
      return rest;
    });
    pollingJobsRef.current.delete(jobId);
    delete backoffRef.current[jobId];
    delete listenersRef.current[jobId];
  }, []);

  /**
   * Polling d'un job avec setTimeout récursif (évite chevauchement)
   * Ne s'exécute que si cet onglet est le leader
   */
  const pollJob = useCallback(async (jobId, userId, isLeaderTab) => {
    // Vérifier si on doit continuer le polling
    if (!pollingJobsRef.current.has(jobId)) {
      return; // Polling arrêté
    }

    // Seul l'onglet leader effectue les requêtes réseau
    // Les autres onglets reçoivent les mises à jour via BroadcastChannel
    if (!isLeaderTab) {
      // Onglet non-leader : réessayer plus tard au cas où on devienne leader
      setTimeout(() => {
        const currentlyLeader = leaderTabIdRef.current === tabIdRef.current;
        if (pollingJobsRef.current.has(jobId)) {
          pollJob(jobId, userId, currentlyLeader);
        }
      }, POLL_INTERVAL_MS * 2);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/jobs/${jobId}/status`);
      const jobData = response.data;
      
      // Réinitialiser le backoff après succès
      backoffRef.current[jobId] = INITIAL_BACKOFF_MS;
      
      // Mettre à jour l'état (sera broadcasté aux autres onglets)
      updateJob(jobId, {
        status: jobData.status,
        total_sites: jobData.total_sites,
        completed_sites: jobData.completed_sites,
        sites_status: jobData.sites_status,
        total_cost_eur: jobData.total_cost_eur,
        verbose_log: jobData.verbose_log,
        cluster_total_links_injected: jobData.cluster_total_links_injected,
        scrape_warning: jobData.scrape_warning,
      });

      // Vérifier si le job est terminé
      const finalStatuses = ['completed', 'error', 'cancelled'];
      if (finalStatuses.includes(jobData.status)) {
        pollingJobsRef.current.delete(jobId);
        updateJob(jobId, { polling: false, finishedAt: Date.now() });
        broadcast(MSG_TYPE.JOB_COMPLETED, { jobId, data: { status: jobData.status } });
        return; // Arrêter le polling
      }

      // Planifier le prochain poll avec setTimeout (pas setInterval)
      const interval = isTabVisible ? POLL_INTERVAL_MS : POLL_INTERVAL_HIDDEN_MS;
      setTimeout(() => {
        const stillLeader = leaderTabIdRef.current === tabIdRef.current;
        pollJob(jobId, userId, stillLeader);
      }, interval);
      
    } catch (error) {
      console.error(`[JobContext] Poll error for job ${jobId}:`, error);
      
      // Backoff exponentiel après erreur
      const currentBackoff = backoffRef.current[jobId] || INITIAL_BACKOFF_MS;
      const nextBackoff = Math.min(currentBackoff * 2, MAX_BACKOFF_MS);
      backoffRef.current[jobId] = nextBackoff;
      
      // Si erreur 404 ou 403, arrêter le polling
      if (error.response?.status === 404 || error.response?.status === 403) {
        pollingJobsRef.current.delete(jobId);
        updateJob(jobId, { polling: false, error: 'Job inaccessible' });
        return;
      }
      
      // Réessayer avec backoff
      if (pollingJobsRef.current.has(jobId)) {
        setTimeout(() => pollJob(jobId, userId), currentBackoff);
      }
    }
  }, [isTabVisible, updateJob, broadcast]);

  /**
   * Démarre le polling pour un job
   */
  const startPolling = useCallback((jobId, userId, initialData = {}) => {
    // Éviter les doublons de polling
    if (pollingJobsRef.current.has(jobId)) {
      console.log(`[JobContext] Polling already active for job ${jobId}`);
      return;
    }

    pollingJobsRef.current.add(jobId);
    backoffRef.current[jobId] = INITIAL_BACKOFF_MS;
    
    // Initialiser l'état du job
    updateJob(jobId, {
      ...initialData,
      jobId,
      userId,
      polling: true,
      startedAt: Date.now(),
    });

    // Démarrer le polling (en passant le statut de leader)
    const currentlyLeader = leaderTabIdRef.current === tabIdRef.current;
    pollJob(jobId, userId, currentlyLeader);
  }, [pollJob, updateJob]);

  /**
   * Arrête le polling pour un job
   */
  const stopPolling = useCallback((jobId) => {
    pollingJobsRef.current.delete(jobId);
    updateJob(jobId, { polling: false });
  }, [updateJob]);

  /**
   * Enregistre un nouveau job (appelé après création réussie)
   */
  const registerJob = useCallback((jobId, userId, jobData) => {
    console.log(`[JobContext] Registering new job ${jobId}`);
    
    // Broadcaster aux autres onglets
    broadcast(MSG_TYPE.JOB_REGISTERED, {
      jobId,
      data: {
        status: 'pending',
        total_sites: jobData.total_sites || 0,
        completed_sites: 0,
        sites_status: jobData.sites || [],
        total_cost_eur: 0,
      }
    });
    
    startPolling(jobId, userId, {
      status: 'pending',
      total_sites: jobData.total_sites || 0,
      completed_sites: 0,
      sites_status: jobData.sites || [],
      total_cost_eur: 0,
    });
  }, [startPolling, broadcast]);

  /**
   * Restaure les jobs actifs depuis le backend (au démarrage/rechargement)
   */
  const restoreActiveJobs = useCallback(async (userId) => {
    if (!userId) return;
    
    try {
      const response = await axios.get(`${API}/api/jobs`);
      const jobs = response.data || [];
      
      // Filtrer les jobs encore actifs
      const activeStatuses = ['pending', 'running', 'scrape_failed'];
      const activeJobsList = jobs.filter(j => activeStatuses.includes(j.status));
      
      console.log(`[JobContext] Restoring ${activeJobsList.length} active jobs`);
      
      for (const job of activeJobsList) {
        const jobId = job.job_id;
        if (!pollingJobsRef.current.has(jobId)) {
          startPolling(jobId, userId, {
            status: job.status,
            total_sites: job.total_sites,
            completed_sites: job.completed_sites,
            total_cost_eur: job.total_cost_eur,
          });
        }
      }
    } catch (error) {
      console.error('[JobContext] Failed to restore jobs:', error);
    }
  }, [startPolling]);

  /**
   * Nettoie tous les jobs (déconnexion)
   */
  const clearAllJobs = useCallback(() => {
    pollingJobsRef.current.clear();
    backoffRef.current = {};
    listenersRef.current = {};
    setActiveJobs({});
  }, []);

  /**
   * Récupère un job par son ID
   */
  const getJob = useCallback((jobId) => {
    return activeJobsRef.current[jobId] || null;
  }, []);

  /**
   * Vérifie si un job est en cours de polling
   */
  const isPolling = useCallback((jobId) => {
    return pollingJobsRef.current.has(jobId);
  }, []);

  /**
   * Récupère tous les jobs actifs (en cours)
   */
  const getActiveJobsList = useCallback(() => {
    return Object.values(activeJobsRef.current).filter(j => 
      ['pending', 'running', 'scrape_failed'].includes(j.status)
    );
  }, []);

  const value = {
    // État
    activeJobs,
    
    // Actions
    registerJob,
    startPolling,
    stopPolling,
    updateJob,
    removeJob,
    restoreActiveJobs,
    clearAllJobs,
    
    // Queries
    getJob,
    isPolling,
    getActiveJobsList,
    
    // Subscriptions
    subscribeToJob,
  };

  return (
    <JobContext.Provider value={value}>
      {children}
    </JobContext.Provider>
  );
};

export default JobContext;
